import { useQuery } from '@tanstack/react-query';

/**
 * The platform's General settings, from the public `site-config` read model.
 *
 * Shares the query key with `useDateFormatSetting`, so the shell's fetch
 * serves the forms too. Public rather than `/platform/settings/general`
 * because a society admin editing their own society needs the platform's
 * country and zone as defaults and has no platform session.
 */

const API_BASE = import.meta.env['VITE_API_BASE_URL'] ?? '';

export interface PlatformGeneral {
  siteName: string;
  logoUrl: string;
  faviconUrl: string;
  logoUrlDark: string;
  faviconUrlDark: string;
  consoleBrandDisplay: 'both' | 'logo' | 'name';
  consoleLogoHeight: number;
  supportEmail: string;
  supportPhone: string;
  helpUrl: string;
  dateFormat: string;
  timeFormat: string;
  country: string;
  timezone: string;
}

export function usePlatformGeneral(): PlatformGeneral | undefined {
  const query = useQuery({
    queryKey: ['platform', 'site-config'],
    queryFn: async () => {
      // `no-store`: the response carries a 60-second public cache for the
      // marketing site, and the console must not sit behind it — a branding
      // change saved here has to show on the next refetch, not a minute later.
      const response = await fetch(`${API_BASE}/public/site-config`, { cache: 'no-store' });
      if (!response.ok) throw new Error('site-config unavailable');
      const payload = (await response.json()) as { success: boolean; data?: { general?: PlatformGeneral } };
      return payload.data ?? {};
    },
    staleTime: 5 * 60_000,
    retry: false,
  });
  return query.data?.general;
}
